import {useTranslation} from "react-i18next";
import {useState, useCallback, useEffect, useRef} from "react";
import NavBreadcrum from "components/NavBreadcrum";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import navs from "./navs";
import axiApi from "ApiCaller";
import LinearScatterChart from "../data-view/components/LinearScatterChart";
import ContainersGraphFilters from "./containers-graph/ContainersGrahpFilters";
import Spinner from "components/loaders/spinner";
import useCustomTranslation from "core/customTranslation";
import translations from "./translations";

const ReportContainerPickedUpGraph = () => {
    const {t: Tr} = useTranslation("routes");
    const {t: Tg} = useTranslation("grafics");
    const {t: tC} = useCustomTranslation(translations);
    const title = tC("graph.title");
    const bread = [
        {name: Tr("labels.reports"), url: "/reports/", current: false},
        {name: title, current: true},
    ];

    const [currentFilters, setCurrentFilters] = useState({});
    const filterOptions = useRef({
        groups: [],
        zones: [],
        routes: [],
        islands: [],
    });
    const [measures, setMeasures] = useState([]);
    const [statsData, setStatsData] = useState({});
    const fillingRefs = useRef({});

    const defaultGraphs = 10;

    const [visibleCharts, setVisibleCharts] = useState(defaultGraphs);
    const [counter, setCounter] = useState(0);

    const [isLoading, setIsLoading] = useState(false);

    const handleShowMore = () => {
        setVisibleCharts((prev) => prev + defaultGraphs);
    };

    const handleShowLess = () => {
        setVisibleCharts((prev) => (prev > defaultGraphs ? prev - defaultGraphs : defaultGraphs));
    };

    useEffect(() => {
        document.title = title;
        axiApi.get("/api/reports/containers/filters/").then((resp) => {
            if (resp.data.status) {
                filterOptions.current = {
                    groups: resp.data.groups || [],
                    zones: resp.data.zones || [],
                    routes: resp.data.routes || [],
                    islands: resp.data.islands || [],
                };
                setCurrentFilters((prev) => ({...prev}));
            }
        });
    }, [title]);

    const handleFilterChange = useCallback((newFilters) => {
        const mappedFilters = {
            ...newFilters,
            group: newFilters.group_id,
            zone: newFilters.zone_id,
            route: newFilters.route_id,
            island: newFilters.island_id,
            min: newFilters.min_fill,
            max: newFilters.max_fill,
        };
        delete mappedFilters.group_id;
        delete mappedFilters.zone_id;
        delete mappedFilters.route_id;
        delete mappedFilters.island_id;
        delete mappedFilters.min_fill;
        delete mappedFilters.max_fill;

        setCurrentFilters(mappedFilters);
        setMeasures({});
        setVisibleCharts(defaultGraphs);
    }, []);

    const applyFilters = () => {
        setIsLoading(true);
        // console.log("Aplicando filtros:", currentFilters);
        axiApi
            .post("/api/reports/containers/filters/grahp/", currentFilters)
            .then((resp) => {
                setTimeout(() => {
                    const measures = resp.data?.measures || {};
                    const stats = resp.data?.stats || {};

                    setMeasures(measures);
                    setStatsData(stats);
                    setCounter(Object.keys(measures).length);
                }, 400);
            })
            .catch((e) => {
                console.error("Error al obtener datos:", e);
                setMeasures({});
                setStatsData({});
                setCounter(0);
            })
            .finally(() => setIsLoading(false));
    };

    const handleResetZoom = () => {
        Object.values(fillingRefs.current).forEach((ref) => {
            if (ref) {
                ref.resetZoom();
            }
        });
    };

    const optionsCharts = (_chartId) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top",
            },
            title: {
                display: true,
            },
            zoom: {
                pan: {
                    enabled: false,
                    mode: "x",
                },
                zoom: {
                    wheel: {
                        enabled: false,
                    },
                    pinch: {
                        enabled: false,
                    },
                    scaleMode: "x",
                    mode: "x",
                    drag: {
                        enabled: true,
                        backgroundColor: "rgba(54, 162, 235, 0.3)",
                        borderColor: "rgba(54, 162, 235, 0.5)",
                        borderWidth: 1,
                    },
                    onZoomComplete: ({chart}) => {
                        const xScale = chart.scales.x;
                        const range = xScale.max - xScale.min;

                        if (range <= 1000 * 60 * 60) {
                            xScale.options.time.unit = "minute";
                        } else if (range <= 1000 * 60 * 60 * 24) {
                            xScale.options.time.unit = "hour";
                        } else if (range <= 1000 * 60 * 60 * 24 * 30) {
                            xScale.options.time.unit = "day";
                        } else {
                            xScale.options.time.unit = "month";
                        }
                        chart.update("none");
                    },
                },
            },
        },
        scales: {
            y: {
                type: "linear",
                beginAtZero: true,
                min: 0,
                max: 100,
            },
            x: {
                beginAtZero: true,
                type: "time",
                time: {
                    unit: "day",
                    displayFormats: {
                        hour: "HH:mm",
                        minute: "HH:mm",
                    },
                    tooltipFormat: "DD/MM/YYYY HH:mm:ss",
                },
                adapters: {
                    date: {
                        locale: "es",
                    },
                },
            },
        },
    });

    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <div className="row">
                <div className="col-12">
                    <h3 className="mt-2">{title}</h3>
                </div>
                <div className="col-12 mb-3">
                    <ContainersGraphFilters
                        onFilterChange={handleFilterChange}
                        initialFilters={{}}
                        filterOptions={filterOptions.current}
                        onApplyFilters={applyFilters}
                        counter={counter}
                    />
                </div>

                <div className="row">
                    <div className="col-12">
                        {/* <pre>
                            {JSON.stringify(currentFilters, null, 2)}
                        </pre> */}
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 mt-4">
                        <hr className="mb-4" />
                        {isLoading ? (
                            <div className="d-flex justify-content-center my-5">
                                <Spinner text={tC("common.loading")} />
                            </div>
                        ) : measures && Object.keys(measures).length > 0 ? (
                            <>
                                {/* Mostrar solo las primeras N gráficas según visibleCharts */}
                                {Object.keys(measures)
                                    .slice(0, visibleCharts)
                                    .map((device, deviceIndex) => (
                                        <div key={deviceIndex} className="row">
                                            <div className="col-12">
                                                <div className="row">
                                                    <div className="col-12 d-flex justify-content-between align-items-center">
                                                        <h5 className="m-0">
                                                            {tC("graph.uuid")}: {device} - UID: {measures[device].uid || ""}
                                                        </h5>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={handleResetZoom}>
                                                            <FontAwesomeIcon icon="rotate-right" /> {tC("graph.zoom")}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="col-12 chart-limited">
                                                    {/* {measures[device].filling &&
                                                    measures[device].filling
                                                        .length > 0 ? ( */}
                                                    <LinearScatterChart
                                                        title={Tg("headers.containers.filling")}
                                                        fillings={measures[device].filling || []}
                                                        alerts={measures[device].alerts || {}}
                                                        chartRef={(ref) => {
                                                            fillingRefs.current[device] = ref;
                                                        }}
                                                        options={optionsCharts("chartF")}
                                                    />
                                                    {/* ) : (
                                                        <div className="alert alert-info">
                                                            No se encuentran
                                                            datos de llenado
                                                            para este
                                                            dispositivo.
                                                        </div>
                                                    )} */}
                                                </div>

                                                <div className="col-12 mt-3 d-flex justify-content-center">
                                                    <div
                                                        className="card p-3"
                                                        style={{
                                                            width: "800px",
                                                        }}>
                                                        <h6 className="text-center mb-2">{tC("graph.statistics")}</h6>
                                                        <table className="table table-striped table-sm">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th
                                                                        className="text-center"
                                                                        style={{
                                                                            width: "200px",
                                                                        }}>
                                                                        {tC("graph.minimum")}
                                                                    </th>
                                                                    <th
                                                                        className="text-center"
                                                                        style={{
                                                                            width: "200px",
                                                                        }}>
                                                                        {tC("graph.average")}
                                                                    </th>
                                                                    <th
                                                                        className="text-center"
                                                                        style={{
                                                                            width: "200px",
                                                                        }}>
                                                                        {tC("graph.maximum")}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td className="text-center">
                                                                        {statsData[device] && statsData[device][0]
                                                                            ? Math.round(statsData[device][0].min)
                                                                            : "-"}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {statsData[device] && statsData[device][0]
                                                                            ? Math.round(statsData[device][0].mean * 100) / 100
                                                                            : "-"}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {statsData[device] && statsData[device][0]
                                                                            ? Math.round(statsData[device][0].max)
                                                                            : "-"}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="mt-4 mb-4">
                                                <hr />
                                            </div>
                                        </div>
                                    ))}

                                {/* Botones para mostrar más o menos gráficas */}
                                <div className="row mt-3 mb-5">
                                    <div className="col-12 d-flex justify-content-center">
                                        {visibleCharts < Object.keys(measures).length && (
                                            <button className="btn btn-outline-primary me-3" onClick={handleShowMore}>
                                                <FontAwesomeIcon icon="plus" /> {tC("graph.showMore")}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="alert alert-info">{tC("graph.noData")}</div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReportContainerPickedUpGraph;
